import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/errors';
import { Route42Logo } from '../components/brand/Route42Logo';
import { useAuth } from './AuthProvider';

const ATTRIBUTE_LABELS: Record<string, string> = {
  name: 'Full name',
  given_name: 'First name',
  family_name: 'Last name',
  email: 'Email',
  phone_number: 'Phone number',
  preferred_username: 'Preferred username',
};

const ATTRIBUTE_AUTOCOMPLETE: Record<string, string> = {
  name: 'name',
  given_name: 'given-name',
  family_name: 'family-name',
  email: 'email',
  phone_number: 'tel',
  preferred_username: 'username',
};

export function SignInPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [requiredAttributeValues, setRequiredAttributeValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as { from?: string } | null)?.from ?? '/start';

  if (auth.loading) {
    return (
      <main className="r42-boot" aria-live="polite">
        <Route42Logo variant="mark" />
        <p>Checking your Route42 session…</p>
      </main>
    );
  }

  if (auth.profile) return <Navigate to={from} replace />;
  if (auth.emailVerificationRequired) return <Navigate to="/verify-email" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (auth.challenge === 'NEW_PASSWORD_REQUIRED') {
        if (newPassword !== confirmNewPassword) {
          setError('New password and confirmation do not match.');
          return;
        }

        const userAttributes: Record<string, string> = {};
        for (const attribute of auth.challengeRequiredAttributes) {
          const value = requiredAttributeValues[attribute]?.trim() ?? '';
          if (!value) {
            setError(`${attributeLabel(attribute)} is required.`);
            return;
          }
          userAttributes[attribute] = value;
        }
        await auth.confirmPassword(newPassword, userAttributes);
      } else {
        const challenge = await auth.signIn(username, password);
        if (challenge) return;
      }
      navigate(from, { replace: true });
    } catch (reason) {
      if (reason instanceof ApiError && reason.code === 'EMAIL_NOT_VERIFIED') {
        navigate('/verify-email', { replace: true });
        return;
      }
      setError(reason instanceof Error ? reason.message : 'Route42 could not sign you in.');
    }
  }

  const activating = auth.challenge === 'NEW_PASSWORD_REQUIRED';

  return (
    <main className="r42-auth-page">
      <section className="r42-auth-card">
        <div className="r42-auth-identity">
          <div className="r42-auth-brand"><Route42Logo variant="lockup" /></div>
          <span className="r42-eyebrow r42-auth-tagline">Machine Intelligence</span>
        </div>
        <div className="r42-auth-task">
          <h1>{activating ? 'Create your password' : 'Sign in'}</h1>
          <p>
            {activating
              ? auth.challengeRequiredAttributes.length
                ? 'Complete your profile and choose a permanent password to finish activating your Route42 identity.'
                : 'Set a new password to finish activating your Route42 identity.'
              : 'Use your Route42 account to access your machines.'}
          </p>
        </div>
        <form onSubmit={(event) => void submit(event)}>
          {activating ? (
            <>
              {auth.challengeRequiredAttributes.map((attribute) => (
                <label key={attribute}>
                  {attributeLabel(attribute)}
                  <input
                    autoComplete={ATTRIBUTE_AUTOCOMPLETE[attribute] ?? 'off'}
                    value={requiredAttributeValues[attribute] ?? ''}
                    onChange={(event) => setRequiredAttributeValues((current) => ({
                      ...current,
                      [attribute]: event.target.value,
                    }))}
                    required
                  />
                </label>
              ))}
              <PasswordField
                id="route42-new-password"
                label="New password"
                autoComplete="new-password"
                value={newPassword}
                onChange={setNewPassword}
              />
              <PasswordField
                id="route42-confirm-new-password"
                label="Confirm new password"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
              />
            </>
          ) : (
            <>
              <label>
                Email or username
                <input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required />
              </label>
              <PasswordField
                id="route42-password"
                label="Password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
              />
              <div className="r42-auth-forgot"><Link to="/forgot-password">Forgot password?</Link></div>
            </>
          )}
          {error ? <div className="r42-form-error" role="alert">{error}</div> : null}
          <button className="btn r42-btn-primary w-100" disabled={auth.loading} type="submit">
            {auth.loading ? 'Signing in…' : activating ? 'Continue' : 'Sign in'}
          </button>
        </form>
      </section>
    </main>
  );
}

function PasswordField({
  id,
  label,
  value,
  autoComplete,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  autoComplete: string;
  onChange: (value: string) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="r42-auth-control">
      <label htmlFor={id}>{label}</label>
      <div className="r42-password-input">
        <input
          id={id}
          type={revealed ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
        />
        <button
          className="r42-password-toggle"
          type="button"
          aria-label={`${revealed ? 'Hide' : 'Show'} ${label.toLocaleLowerCase()}`}
          aria-pressed={revealed}
          onClick={() => setRevealed((current) => !current)}
        >
          <i className={`bi ${revealed ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function attributeLabel(attribute: string): string {
  return ATTRIBUTE_LABELS[attribute] ?? attribute.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toLocaleUpperCase());
}
